import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  InputGroup,
  InputGroupText,
} from "reactstrap";
import styles from "./MetricsModal.module.scss";
import { BiRupee } from "react-icons/bi";
import { MdOutlineClose } from "react-icons/md";

import MeritoButton from "../../../../../components/common/MeritoButtons/MeritoButtons";
import {
  getAgencyDashboardMetrics,
  getPartnerLogo,
  parseNumberForDB,
} from "../../../../../components/functions/utilities";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../../redux/store";
import { RestS } from "../../../../../rest/rest-service";
import moment from "moment";
import OutlinedFormInput from "../../../../Finance/components/OutlinedFormInput/OutlinedFormInput";

type MonthRow = { id: string; label: string; isCurrent?: boolean };
export type SavedValues = Record<string, number | null>; // key: "YYYY-MM"

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSave: (values: SavedValues) => void;
  title: string;
  icon?: React.ReactNode;
  unitSymbol?: any;
  seedValue?: number | null;
  maxValue?: number;
  contextKey?: string;
  data: any;
  brandId?: string | number;
};

const INDIAN_LOCALE = "en-IN";
const DEFAULT_MAX = 100000000;

const fmtIN = (n: number) => n.toLocaleString(INDIAN_LOCALE);
const clamp = (n: number, max: number) => Math.max(0, Math.min(n, max));

function monthLabel(date: Date) {
  return date.toLocaleString("en-US", { month: "long" });
}
function yyyyMm(date: Date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
}

/** Curr + next 3 months (4 rows) */
function makeRows(): MonthRow[] {
  const now = new Date();
  const ms = [0, 1, 2, 3].map(
    (k) => new Date(now.getFullYear(), now.getMonth() + k, 1)
  );
  return ms.map((d, i) => ({
    id: yyyyMm(d),
    label: monthLabel(d),
    isCurrent: i === 0,
  }));
}

function digitsFrom(s: string): string {
  return s?.replace(/\D/g, "");
}

function liveFormatDigits(digits: string, maxValue: number): string {
  if (!digits) return "";
  digits = digits.replace(/^0+(?=\d)/, "");
  const n = Number(digits || "0");
  const clamped = clamp(Number.isFinite(n) ? n : 0, maxValue);
  return fmtIN(clamped);
}

function parseNum(s: string): number {
  return Number(String(s).replace(/,/g, "")) || 0;
}

export default function MetricsModal({
  isOpen,
  brandId,
  onClose,
  onSave,
  title,
  seedValue = null,
  maxValue = DEFAULT_MAX,
  data = null,
}: Props) {
  const rows = useMemo(makeRows, []);
  const [uiVals, setUiVals] = useState<Record<string, string>>({});
  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const [targetData, setTargetData] = useState<any>(null);
  const dispatch = useDispatch();

  // fetch from API when modal opens

  console.log("data in modal", data);

  const handleChange = (id: string, raw: string) => {
    if (raw === "-") {
      setUiVals((p) => ({ ...p, [id]: "-" }));
      return;
    }
    const digits = digitsFrom(raw);
    console.log("here it isssssssssssssssssss", digits);
    if (digits === "") {
      setUiVals((p) => ({ ...p, [id]: "" }));
      return;
    }
    const formatted = liveFormatDigits(digits, maxValue);
    setUiVals((p) => ({ ...p, [id]: formatted }));
    requestAnimationFrame(() => {
      const el = inputRefs.current[id];
      if (el) {
        const len = formatted.length;
        el.setSelectionRange(len, len);
      }
    });
  };

  const handleBlur = (id: string) => {
    setUiVals((p) => {
      const v = p[id];
      if (v === "-" || v === "") return { ...p, [id]: "" };
      return p;
    });
  };

  const handleSave = () => {
    const targetData = [];

    for (const r of rows) {
      const v = uiVals[r.id];
      if ((v && v !== "-") || v === "0") {
        targetData.push({
          metricId: data?.metricId,
          clientCompanyId: Number(brandId),
          value: clamp(parseNum(v), maxValue),
          monthYear: `${r.id}-01`,
        });
        getAgencyDashboardMetrics(dispatch, null);
      } else if (!v || v === "-") {
        targetData.push({
          metricId: data?.metricId,
          clientCompanyId: Number(brandId),
          value: null,
          monthYear: `${r.id}-01`,
        });
        getAgencyDashboardMetrics(dispatch, null);
      }
    }

    console.log("targetData", targetData);
    RestS.setAgencyDashboardTarget(targetData).then((res) => {
      console.log("setAgencyDashboardTarget res", res);
    });

    onClose();
  };

  const icon = useSelector((state: RootState) =>
    getPartnerLogo(state, data?.ipId)
  );

  useEffect(() => {
    if (isOpen) {
      RestS.getAgencyDashboardTarget(data?.metricId, Number(brandId)).then(
        (targetData) => {
          setTargetData(targetData);

          const prefilled: Record<string, string> = {};
          for (const r of rows) {
            const match = targetData?.data?.find(
              (t: any) => moment(t.monthYear).format("YYYY-MM") === r.id
            );

            if (match && match.value != null && match.value >= 0) {
              prefilled[r.id] = fmtIN(match.value);
            } else if (r.isCurrent) {
              if (data?.currMonth) {
                prefilled[r.id] = fmtIN(data.currMonth);
              }
            } else {
              prefilled[r.id] = "-";
            }
          }
          console.log("prefilled", prefilled);
          setUiVals(prefilled);
        }
      );
    }
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      centered
      size="md"
      backdrop="static"
    >
      <div className={styles.container}>
        <div className={styles.titleBar}>
          <div className={styles.title}>
            <div className={styles.titleContent}>
              <div className={styles.iconContainer}>
                {icon && (
                  <span className={styles.icon}>
                    <img src={icon} alt="channel logo" />
                  </span>
                )}
              </div>

              <span>{title}</span>
            </div>
          </div>
          <MeritoButton
            type="tertiary"
            icon={<MdOutlineClose />}
            isIconToRight={false}
            onlyIcon={true}
            size="md"
            onClick={onClose}
            aria-label="Close"
          ></MeritoButton>
        </div>

        <div className={styles.monthsContainer}>
          {rows.map((r) => (
            <div key={r.id} className={styles.row}>
              <div className={styles.label}>
                <span className={styles.monthName}>{r.label}</span>
                {r.isCurrent && (
                  <div className={styles.badgeContainer}>
                    <span className={styles.badge}>Current</span>
                  </div>
                )}
              </div>

              {/* <div className={styles.inputContainer}> */}
              {/* <div className={styles.prefix}>
                  <BiRupee className={styles.rupeeIcon} />
                </div> */}
              <OutlinedFormInput
                // className={styles.textInput}
                // innerRef={(el) => (inputRefs.current[r.id] = el)}
                value={parseNumberForDB(uiVals[r.id])}
                name="Update Target Input"
                error={null}
                touched={false}
                // value={"6487326487326473246"}
                unitPlaceHolder="₹"
                placeholder=""
                onChange={(e) => handleChange(r.id, e.target.value)}
                onBlur={() => handleBlur(r.id)}
                // inputMode="numeric"
                inpuType="number"
                isCurrency={true}
                aria-label={`${r.label} amount`}
              />
              {/* </div> */}
            </div>
          ))}
        </div>

        <ModalFooter className={styles.footer}>
          <MeritoButton type="tertiary" onClick={onClose}>
            Cancel
          </MeritoButton>
          <MeritoButton type="primary-1" onClick={handleSave}>
            Update
          </MeritoButton>
        </ModalFooter>

        {/* <ModalBody>
        <div className={styles.grid}>
          {rows.map((r) => (
            <div key={r.id} className={styles.row}>
              <div className={styles.label}>
                <span>{r.label}</span>
                {r.isCurrent && <span className={styles.badge}>Current</span>}
              </div>

              <InputGroup className={styles.input}>
                <InputGroupText className={styles.prefix}>
                  {unitSymbol}
                </InputGroupText>
                <Input
                  className={styles.textInput}
                  innerRef={(el) => (inputRefs.current[r.id] = el)}
                  value={uiVals[r.id] ?? "-"}
                  placeholder="-"
                  onChange={(e) => handleChange(r.id, e.target.value)}
                  onBlur={() => handleBlur(r.id)}
                  inputMode="numeric"
                  aria-label={`${r.label} amount`}
                />
              </InputGroup>
            </div>
          ))}
        </div>
      </ModalBody>

      <ModalFooter className={styles.footer}>
        <MeritoButton type="tertiary" onClick={onClose}>
          Cancel
        </MeritoButton>
        <MeritoButton type="primary-1" onClick={handleSave}>
          Update
        </MeritoButton>
      </ModalFooter> */}
      </div>
    </Modal>
  );
}
